import { COURSE_HOLE_COUNT, normalizeHoleValues } from './courseScorecard.js';

export function calculateCourseAndPlayingHandicapForView(
  handicapIndex: number,
  course: { slope_rating: number; course_rating: number; par: number },
): { courseHandicapValue: number; playingHandicapValue: number } {
  const rawCourseHandicap =
    (Number(handicapIndex || 0) * Number(course.slope_rating || 113)) / 113 +
    (Number(course.course_rating || 0) - Number(course.par || 0));
  const courseHandicapValue = Math.round(rawCourseHandicap);
  return {
    courseHandicapValue,
    playingHandicapValue: courseHandicapValue,
  };
}

export function resolveAppliedHandicapForView(
  score: { course_handicap?: number } | null,
  handicapIndex: number,
  course: { slope_rating: number; course_rating: number; par: number },
): { courseHandicapValue: number; playingHandicapValue: number } {
  if (score && Number.isFinite(Number(score.course_handicap))) {
    const appliedCourseHandicap = Math.round(Number(score.course_handicap));
    return {
      courseHandicapValue: appliedCourseHandicap,
      playingHandicapValue: appliedCourseHandicap,
    };
  }
  return calculateCourseAndPlayingHandicapForView(handicapIndex, course);
}

export function assignMatchStrokesForView(
  playerAId: string,
  playerACourseHandicap: number,
  playerBId: string,
  playerBCourseHandicap: number,
  holeHandicaps: number[],
): Record<string, number[]> {
  const strokesByPlayer: Record<string, number[]> = {
    [playerAId]: Array.from({ length: COURSE_HOLE_COUNT }, () => 0),
    [playerBId]: Array.from({ length: COURSE_HOLE_COUNT }, () => 0),
  };

  const playerACourse = Math.round(Number(playerACourseHandicap || 0));
  const playerBCourse = Math.round(Number(playerBCourseHandicap || 0));
  const diff = playerACourse - playerBCourse;
  if (diff === 0) {
    return strokesByPlayer;
  }

  const receivingPlayerId = diff > 0 ? playerAId : playerBId;
  const strokesToAllocate = Math.min(Math.abs(diff), 18);
  const holesByDifficulty = normalizeHoleValues(holeHandicaps, 1)
    .map((handicap, index) => ({ handicap, index }))
    .sort((left, right) => left.handicap - right.handicap);

  for (let strokeNumber = 0; strokeNumber < strokesToAllocate; strokeNumber += 1) {
    const holeIndex = holesByDifficulty[strokeNumber % COURSE_HOLE_COUNT]?.index ?? 0;
    const current = strokesByPlayer[receivingPlayerId]?.[holeIndex] ?? 0;
    strokesByPlayer[receivingPlayerId]![holeIndex] = current + 1;
  }

  return strokesByPlayer;
}

export function calculateCourseStrokesForView(
  courseHandicap: number,
  holeHandicaps: number[],
): number[] {
  const roundedCourseHandicap = Math.max(0, Math.round(Number(courseHandicap || 0)));
  const holesByDifficulty = normalizeHoleValues(holeHandicaps, 1)
    .map((handicap, index) => ({ handicap, index }))
    .sort((left, right) => left.handicap - right.handicap);
  const strokes = Array.from({ length: COURSE_HOLE_COUNT }, () => 0);
  const baseStrokes = Math.floor(roundedCourseHandicap / COURSE_HOLE_COUNT);
  const remainingStrokes = roundedCourseHandicap % COURSE_HOLE_COUNT;

  for (let index = 0; index < COURSE_HOLE_COUNT; index += 1) {
    strokes[index] = baseStrokes;
  }
  for (let index = 0; index < remainingStrokes; index += 1) {
    const holeIndex = holesByDifficulty[index]?.index ?? 0;
    strokes[holeIndex] = (strokes[holeIndex] ?? 0) + 1;
  }

  return strokes;
}

export function calculateAbsentHoleScoresForView(
  playingHandicap: number,
  holePars: number[],
  holeHandicaps: number[],
): number[] {
  const pars = normalizeHoleValues(holePars, 4);
  const handicaps = normalizeHoleValues(holeHandicaps, 1);
  const totalStrokesAbovePar = Number(playingHandicap || 0) + 2;
  const baseStrokes = Math.floor(totalStrokesAbovePar / COURSE_HOLE_COUNT);
  const remainingStrokes = totalStrokesAbovePar % COURSE_HOLE_COUNT;
  const applied = Array.from({ length: COURSE_HOLE_COUNT }, () => baseStrokes);

  const holesByDifficulty = handicaps
    .map((handicap, index) => ({ handicap, index }))
    .sort((left, right) => left.handicap - right.handicap);

  for (let index = 0; index < remainingStrokes; index += 1) {
    const holeIndex = holesByDifficulty[index]?.index ?? 0;
    const current = applied[holeIndex] ?? baseStrokes;
    applied[holeIndex] = current + 1;
  }

  return pars.map((par, index) => par + applied[index]!);
}

export function isHammerHoleForView(holeNumber: number, hammerHole: number | null): boolean {
  return Number.isInteger(hammerHole) && Number(hammerHole) === Number(holeNumber);
}

export function calculatePerHolePointsForView(
  netA: number[],
  netB: number[],
  hammerHole: number | null = null,
): { pointsA: number[]; pointsB: number[] } {
  const pointsA: number[] = [];
  const pointsB: number[] = [];
  for (let hole = 0; hole < COURSE_HOLE_COUNT; hole += 1) {
    const a = Number(netA[hole]);
    const b = Number(netB[hole]);
    const holeValue = isHammerHoleForView(hole + 1, hammerHole) ? 4 : 2;
    if (!Number.isFinite(a) || !Number.isFinite(b)) {
      pointsA.push(0);
      pointsB.push(0);
      continue;
    }
    if (a < b) {
      pointsA.push(holeValue);
      pointsB.push(0);
    } else if (b < a) {
      pointsA.push(0);
      pointsB.push(holeValue);
    } else {
      pointsA.push(holeValue / 2);
      pointsB.push(holeValue / 2);
    }
  }
  return { pointsA, pointsB };
}

export function calculateOverallNetPointsForView(
  totalNetA: number,
  totalNetB: number,
): { pointsA: number; pointsB: number } {
  const netA = Number(totalNetA);
  const netB = Number(totalNetB);
  if (!Number.isFinite(netA) || !Number.isFinite(netB)) {
    return { pointsA: 0, pointsB: 0 };
  }
  if (netA < netB) {
    return { pointsA: 4, pointsB: 0 };
  }
  if (netB < netA) {
    return { pointsA: 0, pointsB: 4 };
  }
  return { pointsA: 2, pointsB: 2 };
}

export function parseHammerHoleInput(value: FormDataEntryValue | null): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= COURSE_HOLE_COUNT ? parsed : null;
}

export function grossScoreClassForView(score: number, par: number): string {
  const diff = Number(score) - Number(par);
  if (!Number.isFinite(diff)) {
    return '';
  }
  if (diff <= -2) {
    return 'score-eagle';
  }
  if (diff === -1) {
    return 'score-birdie';
  }
  if (diff === 1) {
    return 'score-bogey';
  }
  if (diff === 2) {
    return 'score-double';
  }
  if (diff > 2) {
    return 'score-worse';
  }
  return '';
}
